import { useEffect, useMemo, useState, type ReactNode } from 'react';
import type { TemplateBlockInstance, TemplateVersion } from '@/templates/domain/types';
import type { DocumentContext } from '../domain/DocumentContext';
import type { PageSize } from '../domain/PageSize';
import { getPageDimensions } from '../domain/PageSize';
import { A4_WIDTH_PX, COMPACT_VIEWPORT_PX } from '../domain/constants/pageMetrics';
import type { BlockContent, BlockStyle } from '@/blocks/domain/types';
import { DocumentFrame } from './DocumentFrame';
import { PageRender } from './PageRender';
import { countPriceBookRows } from '../domain/priceBook/countPriceBookRows';
import { RENDERER_REGISTRY } from './blocks/__registry';
import { isSplittable } from '../domain/RendererContract';
import { useBlockHeightMeasurement } from '../application/measureBlockHeights';
import { blockPlacementLayoutStyle, PLACEMENT_WIDTH_FULL } from '../application/blockPlacementLayoutStyle';
import { isFullOnlyKind } from '@/blocks/domain/helpers/blockKindPlacementPolicy';
import { rolesUseCategories } from '@/blocks/domain/helpers/roleCategories';
import { buildProvisionalPageLayout } from '../application/buildProvisionalPageLayout';
import { startsOnNewPage } from '../domain/startsOnNewPage';
import { pageLayoutsEqual, paginate, type PageLayout } from '../application/paginate';
import { resolveProseSegments } from '../application/resolveProseSegments';
import { keepsUnboundTokens } from '../application/unboundTokenHtml';
import { pageItemKey } from '../application/pageItemKey';
import { useContainerScale } from '../application/useContainerScale';
import { PageBreakIndicator } from './PageBreakIndicator';
import { BlockRenderErrorBoundary } from './BlockRenderErrorBoundary';
import { useEditorUiStore } from '@/common/presentation/state/useEditorUiStore';
import type { TemplateBackground } from '@/templates/domain/types/TemplateBackground';
import type { TemplateCoverImage } from '@/templates/domain/types/TemplateCoverImage';
import type { TemplateLogo } from '@/templates/domain/types/TemplateLogo';
import {
  enrichDocumentContextWithTemplateCoverImage,
  enrichDocumentContextWithTemplateLogo,
  stripHostLogoFromBranding
} from '../application/documentBranding';
import { isInstanceRenderable } from '../application/isInstanceRenderable';
import { resolveLogoUrl, type ResolveLogoUrlOptions } from '../application/resolveLogoUrl';
import { DocumentAudienceProvider, type DocumentAudience } from '@/document-render/presentation/primitives/useDocumentAudience';
import { DocumentSurfaceProvider } from '@/document-render/presentation/primitives/useDocumentSurface';
import { customUsesSections } from '@/blocks/domain/helpers/customSections';

const PROSE_BLOCK_KINDS = new Set(['scope_of_work', 'comments', 'terms_conditions', 'custom_text', 'custom']);

/** A sectioned Custom block is laid out like a header (atomic), not sliced into prose segments. */
const isSectionedCustom = (content: BlockContent) => content.kind === 'custom' && customUsesSections(content);

interface Props {
  templateVersion: TemplateVersion;
  context: DocumentContext;
  knownPaths?: ReadonlySet<string>;
  mode?: 'editor' | 'export';
  /**
   * Internal vs customer audience. Customer mode strips COST / MARGIN % columns
   * regardless of template configuration (LLAMS-275).
   */
  audience?: DocumentAudience;
  pageSize?: PageSize;
  accentColor?: string;
  pagePadding?: number;
  /** Template page background style. */
  background?: TemplateBackground;
  /** Template settings base font — applied to every HTML page via PageRender. Defaults to Inter. */
  baseFont?: string;
  /** Template company logo (placement + size + alignment). */
  logo?: TemplateLogo | null;
  /**
   * The template's own cover-page image — never the page `background`. Drawn only by a cover_page
   * block, and only when `imageUrl` (or `assetRef`) is a renderable URL; resolve a blob key first.
   */
  coverImage?: TemplateCoverImage | null;
  /**
   * `template-only` — only `logo.url` from template settings (no host branding / RapidWorks fallback).
   * Used by template editor preview and list thumbnails.
   */
  logoResolution?: 'template-only' | 'full';
  /**
   * When false, a missing template and branding logo stays blank. A quote preview
   * (`contextDefaults: 'empty'`) must not substitute the sample RapidWorks mark.
   */
  allowDefaultLogoFallback?: boolean;
  /** `flowing` shows page-break indicators. `paged` is a fixed sheet with overflow hidden. */
  layout?: 'paged' | 'flowing';
  /**
   * Applied to the visible instance only. The measurement pass never receives this wrapper.
   * `isFirstSlice` is false for the later page slices of a split block.
   */
  blockEditingWrapper?: (args: { instanceId: string; kind: string; children: ReactNode; isFirstSlice: boolean }) => ReactNode;
}

type ImportedBlockJson = { content: BlockContent; style: BlockStyle };

export const DocumentRender = ({
  templateVersion,
  context,
  knownPaths,
  mode = 'editor',
  audience = 'internal',
  pageSize = 'A4',
  accentColor,
  pagePadding = 48,
  background,
  baseFont = 'Inter',
  logo,
  coverImage,
  logoResolution = 'full',
  allowDefaultLogoFallback = true,
  layout,
  blockEditingWrapper
}: Props) => {
  const isTemplateOnlyLogo = logoResolution === 'template-only';
  const effectiveContext = useMemo(
    () => (isTemplateOnlyLogo ? stripHostLogoFromBranding(context) : context),
    [context, isTemplateOnlyLogo]
  );
  const logoResolveOptions = useMemo((): ResolveLogoUrlOptions => {
    if (isTemplateOnlyLogo) return { allowContextBranding: false, allowDefaultFallback: false };
    if (!allowDefaultLogoFallback) return { allowDefaultFallback: false };
    return {};
  }, [allowDefaultLogoFallback, isTemplateOnlyLogo]);
  const logoUrl = useMemo(() => resolveLogoUrl(logo, effectiveContext, logoResolveOptions), [effectiveContext, logo, logoResolveOptions]);

  const documentContext = useMemo(
    () => enrichDocumentContextWithTemplateCoverImage(enrichDocumentContextWithTemplateLogo(effectiveContext, logo, logoUrl), coverImage),
    [coverImage, effectiveContext, logo, logoUrl]
  );
  const renderableInstances = useMemo(() => {
    const instances: TemplateBlockInstance[] = [];
    const sorted = [...templateVersion.composition].sort((a, b) => a.sortOrder - b.sortOrder);
    for (const instance of sorted) {
      // `visible=false` and `hideForCustomerTags` both drop the block from the document surface —
      // the same predicate the PDF document uses, so the two outputs cannot disagree.
      if (!isInstanceRenderable(instance, documentContext)) continue;
      const json = instance.importedBlockJson as ImportedBlockJson | null;
      if (!json?.content || !json?.style) continue;
      const renderer = RENDERER_REGISTRY[json.content.kind as keyof typeof RENDERER_REGISTRY];
      if (!renderer) continue;
      instances.push(instance);
    }
    return instances;
  }, [documentContext.customer?.tags, templateVersion.composition]);

  const instanceById = useMemo(() => {
    const map = new Map<string, (typeof renderableInstances)[number]>();
    for (const inst of renderableInstances) {
      map.set(inst.templateBlockInstanceId, inst);
    }
    return map;
  }, [renderableInstances]);

  const splittableBlocksRowCounts = useMemo(() => {
    const map = new Map<string, number>();
    const keepTokens = keepsUnboundTokens(documentContext, mode);
    const resolveOptions =
      knownPaths || keepTokens ? { ...(knownPaths ? { knownPaths } : {}), ...(keepTokens ? { keepTokens } : {}) } : undefined;
    for (const instance of renderableInstances) {
      const json = instance.importedBlockJson as ImportedBlockJson | null;
      if (!json) continue;
      const renderer = RENDERER_REGISTRY[json.content.kind as keyof typeof RENDERER_REGISTRY];
      if (!renderer || !isSplittable(renderer)) continue;

      if (json.content.kind === 'line_items_table') {
        const rowCount = documentContext.lineItems?.length ?? 0;
        if (rowCount > 0) map.set(instance.templateBlockInstanceId, rowCount);
      } else if (json.content.kind === 'roles_responsibilities') {
        // The grouped view ignores `slice` and prints every group, so a block saved with categories
        // stays whole: its `rows` is only a mirror for the CRM seed and would otherwise split it.
        const rowCount = rolesUseCategories(json.content) ? 0 : (json.content.rows?.length ?? 0);
        if (rowCount > 0) map.set(instance.templateBlockInstanceId, rowCount);
      } else if (json.content.kind === 'price_book') {
        const rowCount = countPriceBookRows(documentContext.priceBook, documentContext.previewValueBinding, audience);
        if (rowCount > 0) map.set(instance.templateBlockInstanceId, rowCount);
      } else if (json.content.kind === 'scope_fields') {
        const rowCount = Object.keys(documentContext.scope ?? {}).length;
        if (rowCount > 0) map.set(instance.templateBlockInstanceId, rowCount);
      } else if (PROSE_BLOCK_KINDS.has(json.content.kind) && !isSectionedCustom(json.content)) {
        const proseBody = (json.content as { bodyHtml?: unknown }).bodyHtml;
        const bodyHtml = typeof proseBody === 'string' ? proseBody : '';
        const rowCount = resolveProseSegments(bodyHtml, documentContext, resolveOptions).length;
        if (rowCount > 0) map.set(instance.templateBlockInstanceId, rowCount);
      }
    }
    return map;
  }, [audience, documentContext, knownPaths, mode, renderableInstances]);

  const usableHeightPx = useMemo(() => {
    const { heightMm } = getPageDimensions(pageSize, 'portrait');
    const heightPx = (heightMm * 96) / 25.4;
    return Math.max(0, heightPx - pagePadding * 2);
  }, [pagePadding, pageSize]);

  const contentWidthPx = useMemo(() => {
    const { widthMm } = getPageDimensions(pageSize, 'portrait');
    const widthPx = (widthMm * 96) / 25.4;
    return Math.max(0, widthPx - pagePadding * 2);
  }, [pagePadding, pageSize]);

  const { heights, measurementPass } = useBlockHeightMeasurement(renderableInstances, {
    context: documentContext,
    accentColor,
    knownPaths,
    contentWidthPx
  });

  const { previewDragging } = useEditorUiStore();

  const pageBreakAfterIds = useMemo(() => {
    const ids = new Set<string>();
    for (const instance of renderableInstances) {
      const json = instance.importedBlockJson as ImportedBlockJson | null;
      const kind = json?.content?.kind;
      if (!kind) continue;
      const renderer = RENDERER_REGISTRY[kind];
      if (renderer?.pageBreakAfter) {
        ids.add(instance.templateBlockInstanceId);
      }
    }
    return ids;
  }, [renderableInstances]);

  const pageBreakBeforeIds = useMemo(() => {
    const ids = new Set<string>();
    for (const instance of renderableInstances) {
      if (startsOnNewPage(instance)) ids.add(instance.templateBlockInstanceId);
    }
    return ids;
  }, [renderableInstances]);

  // Cover-aware provisional layout: never put body blocks on the cover page while
  // heights are still measuring (avoids the pre-paginate "all on page 1" flash).
  const provisionalLayout = useMemo(
    () => buildProvisionalPageLayout(renderableInstances, pageBreakAfterIds, pageBreakBeforeIds),
    [pageBreakAfterIds, pageBreakBeforeIds, renderableInstances]
  );

  const [measuredLayout, setMeasuredLayout] = useState<PageLayout | null>(null);

  useEffect(() => {
    // Invalidate measured layout when composition/break rules change so we fall
    // back to provisional (still cover-isolated) until the next paginate completes.
    setMeasuredLayout(null);
  }, [provisionalLayout]);

  useEffect(() => {
    if (previewDragging) return;
    if (renderableInstances.length === 0) {
      setMeasuredLayout((prev) => (prev && prev.pages.length === 0 ? prev : { pages: [] }));
      return;
    }
    if (heights.size !== renderableInstances.length) return;

    let cancelled = false;
    const doPaginate = () => {
      if (cancelled) return;
      const next = paginate({
        instances: renderableInstances,
        heights,
        usableHeightPx,
        splittableBlocksRowCounts,
        pageBreakAfterIds,
        pageBreakBeforeIds
      });
      setMeasuredLayout((prev) => (pageLayoutsEqual(prev, next) ? prev : next));
    };

    let idleHandle: number | undefined;
    let timeoutHandle: ReturnType<typeof setTimeout> | undefined;

    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      idleHandle = (window as unknown as { requestIdleCallback: (cb: () => void) => number }).requestIdleCallback(doPaginate);
    } else {
      // jsdom/vitest fallback
      timeoutHandle = setTimeout(doPaginate, 0);
    }

    return () => {
      cancelled = true;
      if (idleHandle != null && typeof window !== 'undefined' && 'cancelIdleCallback' in window) {
        (window as unknown as { cancelIdleCallback: (id: number) => void }).cancelIdleCallback(idleHandle);
      }
      if (timeoutHandle != null) clearTimeout(timeoutHandle);
    };
  }, [heights, pageBreakAfterIds, pageBreakBeforeIds, previewDragging, renderableInstances, splittableBlocksRowCounts, usableHeightPx]);

  const pages = measuredLayout?.pages ?? provisionalLayout.pages;
  const hasHeaderBlock = useMemo(() => {
    return renderableInstances.some((instance) => {
      const json = instance.importedBlockJson as ImportedBlockJson | null;
      return json?.content?.kind === 'header';
    });
  }, [renderableInstances]);

  const totalPages = pages.length;

  const resolvedLayout = layout ?? (mode === 'editor' ? 'flowing' : 'paged');
  const pageLayoutMode = resolvedLayout === 'flowing' ? 'flow' : 'fixed';
  const isEditor = mode === 'editor';
  const pageWidthPx = pageSize === 'A4' ? A4_WIDTH_PX : (getPageDimensions(pageSize, 'portrait').widthMm * 96) / 25.4;
  // Fit both editor and export (LiveDocumentPreview) to the host width. Heights /
  // pagination are measured at full page content width; without CSS scale, a narrow
  // host (Quote Builder preview column) uses width:100% and reflows text, shifting
  // page breaks and "(continued)" headers vs the wide Document tab preview.
  const { ref: scaleContainerRef, scale: observedScale, measuredWidth } = useContainerScale(pageWidthPx);
  const pageScale = observedScale;
  const isCompact = isEditor && measuredWidth > 0 && measuredWidth < COMPACT_VIEWPORT_PX;

  return (
    <DocumentAudienceProvider audience={audience}>
      <DocumentSurfaceProvider surface={mode}>
        <DocumentFrame mode={mode} scaleContainerRef={scaleContainerRef}>
          {measurementPass}
          {pages.map((page, pageIndex) => {
            const isCoverPage = page.items.some((item) => {
              const instance = instanceById.get(item.instanceId);
              const json = instance?.importedBlockJson as ImportedBlockJson | null;
              return json?.content?.kind === 'cover_page';
            });

            return (
              <div key={`doc-page-wrap-${pageIndex}`} style={resolvedLayout === 'paged' ? { overflow: 'hidden' } : undefined}>
                <PageRender
                  pageNumber={pageIndex + 1}
                  totalPages={totalPages}
                  pageSize={pageSize}
                  pagePadding={pagePadding}
                  background={background}
                  isCoverPage={isCoverPage}
                  baseFont={baseFont}
                  logo={logo}
                  logoUrl={logoUrl}
                  hasHeaderBlock={hasHeaderBlock}
                  layoutMode={pageLayoutMode}
                  scale={pageScale}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: isCoverPage ? 'nowrap' : 'wrap',
                      flexDirection: isCoverPage ? 'column' : 'row',
                      alignItems: isCoverPage ? 'stretch' : 'flex-start',
                      flex: isCoverPage ? 1 : undefined,
                      width: '100%',
                      minHeight: isCoverPage ? '100%' : undefined
                    }}
                  >
                    {page.items.map((item, idx) => {
                      const instance = instanceById.get(item.instanceId);
                      if (!instance) return null;
                      const json = instance.importedBlockJson as ImportedBlockJson | null;
                      if (!json) return null;
                      const renderer = RENDERER_REGISTRY[json.content.kind as keyof typeof RENDERER_REGISTRY];
                      if (!renderer) return null;
                      const Comp = renderer.Component;
                      const isCoverBlock = json.content.kind === 'cover_page';
                      const renderedBlock = (
                        <BlockRenderErrorBoundary instanceId={item.instanceId} blockKind={json.content.kind}>
                          <Comp
                            content={json.content}
                            style={json.style}
                            context={documentContext}
                            accentColor={accentColor}
                            knownPaths={knownPaths}
                            slice={item.slice}
                            viewport={{ compact: isCompact }}
                          />
                        </BlockRenderErrorBoundary>
                      );

                      return (
                        <div
                          key={pageItemKey(item.instanceId, pageIndex, item.slice, idx)}
                          data-testid={`document-render:instance:${item.instanceId}`}
                          data-instance-id={item.instanceId}
                          data-block-kind={json.content.kind}
                          data-visible={instance.visible ? 'true' : 'false'}
                          style={{
                            opacity: instance.visible ? 1 : 0.5,
                            ...(isCoverBlock
                              ? { flex: 1, alignSelf: 'stretch', width: '100%', display: 'flex', flexDirection: 'column' }
                              : blockPlacementLayoutStyle(
                                  isFullOnlyKind(json.content.kind) ? PLACEMENT_WIDTH_FULL : instance.placement?.width
                                ))
                          }}
                        >
                          {blockEditingWrapper
                            ? blockEditingWrapper({
                                instanceId: item.instanceId,
                                kind: json.content.kind,
                                children: renderedBlock,
                                isFirstSlice: !item.slice || item.slice.startIndex === 0
                              })
                            : renderedBlock}
                        </div>
                      );
                    })}
                  </div>
                </PageRender>
                {resolvedLayout === 'flowing' && pageIndex < totalPages - 1 ? <PageBreakIndicator pageNumber={pageIndex + 2} /> : null}
              </div>
            );
          })}
        </DocumentFrame>
      </DocumentSurfaceProvider>
    </DocumentAudienceProvider>
  );
};
